import React from 'react';
import classnames from 'classnames';
import { constants, getFormattedDateTime } from '@eb/datetime';
import gettext from '@eb/gettext';
import momentTimezone from 'moment-timezone';

import { logCheckpointViaAjax } from '@eb/janus';
import CalendarCheckMarkChunkySvg from 'eventbrite_design_system/iconography/icons/CalendarCheckMarkChunky';
import CalendarOffChunkySvg from 'eventbrite_design_system/iconography/icons/CalendarOffChunky';
import CalendarStatusChunkySvg from 'eventbrite_design_system/iconography/icons/CalendarStatusChunky';
import ClockFillSvg from 'eventbrite_design_system/iconography/icons/ClockFill';
import EyeOnChunkySvg from 'eventbrite_design_system/iconography/icons/EyeOnChunky';
import PublishChunkySvg from 'eventbrite_design_system/iconography/icons/PublishChunky';
import StatusDotChunkySvg from 'eventbrite_design_system/iconography/icons/StatusDotChunky';
import {
    ANCHOR_LINK,
    DIVIDER,
} from 'eventbrite_design_system/navListItem/constants';
import { isMediumDown } from 'eventbrite_design_system/utils/mediaQueries';
import {
    ACTION_EVENT_PREVIEW,
    CANCEL_SCHEDULE_EVENT_INTENT,
    DEFAULT_LANGUAGE,
    DISPLAY_SALES_STATUS_CUSTOM,
    EVENT_SALES_STATUS_CUSTOM_MESSAGE,
    EVENT_SALES_STATUS_DEFAULT_VALUES,
    EVENT_SALES_STATUS_ICON_AND_COLOR_MAP,
    EVENT_SALES_STATUS_MESSAGE_CODE_TICKETS_AT_THE_DOOR,
    GA_EVENT_STATUS_DRAFT_LABEL,
    GA_EVENT_STATUS_LIVE_LABEL,
    GA_TRACKING_LOCATION_ON_PAGE_GH,
    HEADER_LABELS,
    ORG_ICON_COLOR,
    ORG_ICON_GREEN_COLOR,
    PUBLISH_EVENT_INTENT,
    SCHEDULE_PUBLISH_EVENT_INTENT,
} from '../../constants';

const FORMAT_INVALID_DATE = 'Invalid date';
const SHORT_DATE_FORMAT = 'DD/MM/YYYY';
const { ISO_8601_DATE_FORMAT } = constants;

const getDateFormated = (utc, timezone, locale) => {
    if (isMediumDown()) {
        return getFormattedDateTime(
            utc,
            false,
            timezone,
            locale,
            'MMM D [•] LT',
        );
    }

    return getFormattedDateTime(utc, false, timezone, locale, 'MMM D');
};

const ActionText = ({ text, className, ...otherProps }) => (
    <span className={className} {...otherProps}>
        {text}
    </span>
);
/* This function is exported for testing purposes */
export const getLabelAndIconToDisplay = (
    {
        salesStatusCode,
        isNotYetOnSale,
        isOnSale,
        isNotYetOnSaleAccessCoded,
        salesStatusMessage,
        salesStatusMessageType,
        salesStatusMessageCode,
        salesStartDate,
    } = {},
    locale = DEFAULT_LANGUAGE,
) => {
    if (!salesStatusCode) {
        return {};
    }

    if (isOnSale || isNotYetOnSaleAccessCoded) {
        // for on sale or not yet on sale access coded status, we will show the status message associated with
        // the status code
        return EVENT_SALES_STATUS_ICON_AND_COLOR_MAP[salesStatusCode];
    } else if (
        salesStatusMessageCode ===
        EVENT_SALES_STATUS_MESSAGE_CODE_TICKETS_AT_THE_DOOR
    ) {
        // If the event is sales ended bacause will be sold at the door ,it will display At The Door
        return EVENT_SALES_STATUS_ICON_AND_COLOR_MAP[salesStatusMessageCode];
    }

    let mapOption = EVENT_SALES_STATUS_DEFAULT_VALUES;
    let salesStartDateFormatted = null;
    let eventStatusLabel = null;

    if (isNotYetOnSale) {
        mapOption = salesStatusCode;
        if (salesStatusMessageType !== DISPLAY_SALES_STATUS_CUSTOM) {
            salesStartDateFormatted = getDateFormated(
                salesStartDate.utc,
                salesStartDate.timezone,
                locale,
            );
        }
    }

    const {
        eventStatusIconColor,
        eventStatusNarrowIconType,
        eventStatusNarrowIconColor,
    } = EVENT_SALES_STATUS_ICON_AND_COLOR_MAP[mapOption];

    if (salesStartDateFormatted) {
        // If salesStartDateFormatted is not null it will concat 'On Sale '
        // with the formatted date to display e.g.: On Sale Feb 9
        eventStatusLabel = gettext('On Sale %(date)s', {
            date: salesStartDateFormatted,
        });
    } else if (salesStatusMessageType === DISPLAY_SALES_STATUS_CUSTOM) {
        // This will display Custom text on the Header
        eventStatusLabel = EVENT_SALES_STATUS_CUSTOM_MESSAGE;
    } else {
        // This will display the default status message e.g.; Sold out, Cancelled, Sales Ended, etc.
        eventStatusLabel = salesStatusMessage;
    }
    return {
        eventStatusLabel,
        eventStatusIconColor,
        eventStatusNarrowIconType,
        eventStatusNarrowIconColor,
    };
};

export const getPreviewEventLinks = (serverUrl, eventId, trackEvent) => [
    {
        url: `${serverUrl}/preview?eid=${eventId}/`,
        target: '_blank',
        narrowIconType: <EyeOnChunkySvg />,
        narrowIconColor: ORG_ICON_COLOR,
        content: HEADER_LABELS.PREVIEW_LABEL,
        onClick: () => {
            trackEvent({
                action: ACTION_EVENT_PREVIEW,
                label: GA_EVENT_STATUS_DRAFT_LABEL,
                dimensions: {
                    locationOnPage: GA_TRACKING_LOCATION_ON_PAGE_GH,
                },
            });
        },
    },
];

export const getViewEventLinks = (serverUrl, eventId, trackEvent) => [
    {
        url: `${serverUrl}/e/${eventId}/`,
        target: '_blank',
        narrowIconType: <EyeOnChunkySvg />,
        narrowIconColor: ORG_ICON_COLOR,
        content: HEADER_LABELS.VIEW_LABEL,
        onClick: () => {
            logCheckpointViaAjax('view_event_listing', serverUrl);
            trackEvent({
                action: ACTION_EVENT_PREVIEW,
                label: GA_EVENT_STATUS_LIVE_LABEL,
                dimensions: {
                    locationOnPage: GA_TRACKING_LOCATION_ON_PAGE_GH,
                },
            });
        },
    },
];

export const getUnpublishedEventActionsMenu = (
    serverUrl,
    isScheduleEventPublishEnabled,
) => {
    const publishEventButtonText = isScheduleEventPublishEnabled
        ? HEADER_LABELS.PUBLIC_NOW_LABEL
        : HEADER_LABELS.PUBLISH_EVENT_LABEL;
    const publishEventButtonClass = classnames({
        'eds-text-color--ui-600': isScheduleEventPublishEnabled,
    });
    let textItems = [
        {
            content: (
                <ActionText
                    text={publishEventButtonText}
                    className={publishEventButtonClass}
                    data-automation="coyote-publish-button"
                />
            ),
            type: ANCHOR_LINK,
            value: PUBLISH_EVENT_INTENT,
        },
    ];

    if (isScheduleEventPublishEnabled) {
        textItems = [
            ...textItems,
            {
                content: (
                    <ActionText
                        text={HEADER_LABELS.SCHEDULE_PUBLISH_EVENT_LABEL}
                        className="eds-text-color--ui-600"
                        data-automation="coyote-schedule-publish-button"
                    />
                ),
                type: ANCHOR_LINK,
                value: SCHEDULE_PUBLISH_EVENT_INTENT,
            },
        ];
    }

    return {
        textItems,
        label: HEADER_LABELS.PUBLISH_EVENT_LABEL,
        narrowIconType: <PublishChunkySvg />,
        narrowIconColor: ORG_ICON_COLOR,
        fallbackUrl: serverUrl,
    };
};

export const getPublishedEventActionsMenu = ({
    serverUrl,
    onUnpublishClick,
    onChangeSalesStatusClick,
    manageNewEventStatusFeatureEnabled,
    eventSalesStatus,
    hasPermissionToManageEventSalesStatus,
    locale,
}) => {
    const newEventStatusLabelIcons = getLabelAndIconToDisplay(
        eventSalesStatus,
        locale,
    );
    const {
        eventStatusLabel = HEADER_LABELS.PUBLISHED_LABEL,
        eventStatusIconColor = ORG_ICON_GREEN_COLOR,
        eventStatusNarrowIconType = <CalendarStatusChunkySvg />,
        eventStatusNarrowIconColor = ORG_ICON_COLOR,
    } = newEventStatusLabelIcons;
    let navItems = [];
    if (!eventSalesStatus?.isSalesEnded) {
        navItems = [
            {
                type: ANCHOR_LINK,
                content: HEADER_LABELS.UNPUBLISH_EVENT_LABEL,

                onClick: (e) => {
                    e.preventDefault();
                    onUnpublishClick();
                    window.location.reload();
                },
            },
        ];

        if (manageNewEventStatusFeatureEnabled) {
            navItems = [
                {
                    type: ANCHOR_LINK,
                    content: HEADER_LABELS.CHANGE_STATUS_LABEL,
                    isDisabled: !hasPermissionToManageEventSalesStatus,
                    onClick: (e) => {
                        e.preventDefault();
                        onChangeSalesStatusClick();
                    },
                },
                ...navItems,
            ];
        }

        if (isMediumDown()) {
            navItems = [
                {
                    content: (
                        <ActionText
                            text={eventStatusLabel}
                            className="eds-text-color--ui-500"
                        />
                    ),
                    type: ANCHOR_LINK,
                    isDisabled: true,
                },
                {
                    type: DIVIDER,
                },
                ...navItems,
            ];
        }
    }

    return {
        navItems,
        iconType: <StatusDotChunkySvg />,
        narrowIconType: eventStatusNarrowIconType,
        narrowIconColor: eventStatusNarrowIconColor,
        iconColor: eventStatusIconColor,
        label: eventStatusLabel,
        fallbackUrl: serverUrl,
        hideDropIconWhenNarrow: true,
    };
};

export const getSchedulePublishEventActionsMenu = (
    serverUrl,
    isScheduleEventPublishEnabled,
    schedulePublishDate,
    schedulePublishTime,
    eventTimeZone,
) => {
    let finalMenu = {
        narrowIconType: <ClockFillSvg />,
        narrowIconColor: ORG_ICON_COLOR,
        iconColor: 'ui-blue',
        label: HEADER_LABELS.PUBLISH_SCHEDULED_EVENT_LABEL,
        fallbackUrl: serverUrl,
        hideDropIconWhenNarrow: true,
        textItems: [
            {
                content: <ActionText text={HEADER_LABELS.PUBLIC_NOW_LABEL} />,
                value: PUBLISH_EVENT_INTENT,
            },
            {
                content: (
                    <ActionText
                        text={HEADER_LABELS.CANCEL_PUBLISH_TIME_LABEL}
                    />
                ),
                value: CANCEL_SCHEDULE_EVENT_INTENT,
            },
        ],
    };

    if (isScheduleEventPublishEnabled) {
        const formatDate = schedulePublishDate?.includes('/')
            ? SHORT_DATE_FORMAT
            : ISO_8601_DATE_FORMAT;
        const properTimezone = momentTimezone(
            schedulePublishDate,
            formatDate,
            true,
        );
        const schedulePublishTimezone =
            properTimezone.tz(eventTimeZone) !== undefined &&
            properTimezone.format() !== FORMAT_INVALID_DATE
                ? properTimezone.tz(eventTimeZone).format('z')
                : '';

        finalMenu = {
            ...finalMenu,
            label: HEADER_LABELS.SCHEDULED_EVENT_LABEL,
            narrowIconType: <CalendarCheckMarkChunkySvg />,
            /*
            Published On and Reschedule (first two items) textListItem are the same, communicated to user differently
            TextList does not allow for duplicate values which is why the values have numbered suffixes
            */
            textItems: [
                {
                    content: (
                        <div>
                            {HEADER_LABELS.PUBLISH_ON_EVENT_LABEL}
                            <p>
                                {gettext(
                                    '%(schedulePublishDate)s at %(schedulePublishTime)s %(schedulePublishTimezone)s',
                                    {
                                        schedulePublishDate,
                                        schedulePublishTime,
                                        schedulePublishTimezone,
                                    },
                                )}
                            </p>
                        </div>
                    ),
                    value: `${SCHEDULE_PUBLISH_EVENT_INTENT}-1`,
                },
                {
                    content: HEADER_LABELS.RESCHEDULE_PUBLISH_EVENT_LABEL,
                    value: `${SCHEDULE_PUBLISH_EVENT_INTENT}-2`,
                },
                {
                    content: HEADER_LABELS.UNSCHEDULE_EVENT_LABEL,
                    value: CANCEL_SCHEDULE_EVENT_INTENT,
                },
                {
                    content: HEADER_LABELS.PUBLIC_NOW_LABEL,
                    value: PUBLISH_EVENT_INTENT,
                },
            ],
        };
    }
    return finalMenu;
};

export const getCanceledEventActionMenu = (serverUrl) => ({
    iconType: <StatusDotChunkySvg />,
    iconColor: 'ui-red',
    narrowIconType: <CalendarOffChunkySvg />,
    narrowIconColor: ORG_ICON_COLOR,
    label: HEADER_LABELS.CANCELED_LABEL,
    fallbackUrl: serverUrl,
    navItems: [],
});

export const getEventStatusMenuItem = ({
    serverUrl,
    eventSalesStatus,
    locale,
}) => {
    const {
        eventStatusLabel,
        eventStatusNarrowIconType,
        eventStatusNarrowIconColor,
        eventStatusIconColor,
    } = getLabelAndIconToDisplay(eventSalesStatus, locale);

    const statusItem = {
        iconType: <StatusDotChunkySvg />,
        iconColor: eventStatusIconColor,
        narrowIconColor: eventStatusNarrowIconColor,
        fallbackUrl: serverUrl,
        narrowIconType: eventStatusNarrowIconType,
        label: eventStatusLabel,
        textItems: [
            {
                content: eventStatusLabel,
                value: '',
            },
        ],
    };

    return statusItem;
};

export const getEndedEventActionMenu = ({ serverUrl }) => ({
    iconType: <StatusDotChunkySvg />,
    iconColor: ORG_ICON_COLOR,
    narrowIconType: <CalendarOffChunkySvg />,
    narrowIconColor: ORG_ICON_COLOR,
    label: HEADER_LABELS.ENDED_EVENT_LABEL,
    fallbackUrl: serverUrl,
    preventMouseOver: true,
    navItems: [],
});
